import {
  checkIfHelpDocumentExists,
  doAssertionRefresh,
  selectUser
} from './stores/ajax';
import {
  Breakpoints,
  download,
  intl,
  NotificationType,
  notify,
  showError
} from 'ehs-react';
import { UserMenuStoreApi } from './stores/menuUser.store';
import { PortletStoreApi } from './App';
import type { icModel } from 'generated-ts-common';
type MenuUser = icModel.saf.infrastructure.model.menu.MenuUser;

/**
 * Retrieves manual file for current portal.
 */
export async function doHelpDocumentInitialization(): Promise<void> {
  const isMobileView = window.innerWidth <= Breakpoints.MOBILE;
  const helpIcon = window.document.getElementById(
    isMobileView ? 'navHelpNavbarBtnMobile' : 'navHelpNavbarBtn'
  );

  if (helpIcon) {
    const portalName = helpIcon.getAttribute('portal') ?? 'patient-portal';
    try {
      // make backend call to decide, whether a manual is available:
      const isAvailable = await checkIfHelpDocumentExists({
        url: PortletStoreApi.getState().url,
        portalName
      });
      if (isAvailable?.data) {
        helpIcon.addEventListener('click', () => {
          download(
            PortletStoreApi.getState().url +
              '&operation=getHelpDocument&portalName=' +
              portalName
          );
        });
      }
    } catch (error) {
      helpIcon.addEventListener('click', () => {
        if (isMobileView) {
          document.getElementById('mobileNaviContentTrigger')?.click();
        }
        showError(error);
      });
    }
    helpIcon.addEventListener('keydown', event => {
      if (event.key === 'Enter') {
        helpIcon.click();
      }
    });
  }
}

/**
 * Handler function, that handles the menuUser selected operation.
 *
 * @param user the selected menu user
 */
export async function userToSwitchSelected(user: MenuUser): Promise<void> {
  PortletStoreApi.setState({ ready: false });
  try {
    const url = await selectUser({
      url: PortletStoreApi.getState().url,
      selectedMenuUser: user
    }).then(response => response.data);

    // assemble correct redirect url & perform it:
    const currentUrl = location.href;
    const slicePos = currentUrl.indexOf('/group');
    location.assign(currentUrl.slice(0, slicePos) + url);
  } catch (e) {
    PortletStoreApi.setState({ ready: true });
    showError(e);
  }
}

/**
 * Clears MySites items for representing users.
 */
export function cleanupNavigationBar(): void {
  const users = UserMenuStoreApi.getState().users;

  // determine portals in store:
  const portalsInStore: Set<string> = new Set();
  users.forEach(user => {
    switch (user.portalAccess) {
      case 'DEFAULT':
        portalsInStore.add('default-portal');
        break;
      case 'PHYSICIAN':
        portalsInStore.add('physician-portal');
        break;
      case 'PATIENT':
        portalsInStore.add('patient-portal');
        break;
      case 'ADMIN':
        // the embedded portal is called "sense-embed", so just putting "sense" here would cause issues
        portalsInStore.add('sense-portal');
        break;
    }
  });

  // now remove the portals from navigation bar:
  ['mySites', 'mySites_mobile'].forEach(portal =>
    removePortals(document.getElementById(portal), Array.from(portalsInStore))
  );
}

/**
 * Removes MySites link elements based on assigned user profiles.
 *
 * @param mySites list of portal link elements
 * @param portalsInStore available portals based on assigned user profiles
 */
function removePortals(
  mySites: HTMLElement | null,
  portalsInStore: Array<string>
): void {
  if (mySites) {
    // get portals:
    const dropDown = mySites.querySelector('.my-sites-menu');

    [...(dropDown?.children ?? [])].forEach(portal => {
      if (
        portalsInStore.some(portalInStore =>
          portal.innerHTML.includes(portalInStore)
        )
      ) {
        // remove every matched "mySites" entry
        dropDown?.removeChild(portal);
      }
    });
    // removes empty mySites
    if (!dropDown?.children.length) {
      mySites.remove();
    }
  }
}

/**
 * This function will trigger the assertion refresh and reschedules itself to the next refresh timestamp.
 *
 * @param dateToRefresh
 */
export function scheduleRefreshAssertionCall(dateToRefresh: Date | null): void {
  const retryCountOnError = 3; // when refresh fails in the backend, allow to retry it (could be a temporary hick-up of IDP server)
  if (dateToRefresh) {
    const delay = Math.max(dateToRefresh.getTime() - new Date().getTime(), 1);
    registerRefreshCallForDelay(delay, retryCountOnError);
  } else {
    // next refresh time is not valid --> try to trigger it in 10 seconds:
    registerRefreshCallForDelay(10 * 1000, retryCountOnError);
  }
}

function registerRefreshCallForDelay(delay: number, retryCount: number) {
  const timeId = '[' + new Date().getTime() + ']';
  console.debug(timeId, 'schedule next doAssertionRefresh in ', delay);
  setTimeout(async function () {
    console.debug(timeId, 'performing doAssertionRefresh');
    try {
      // perform AJAX call to refresh assertion:
      const nextTimeStampAsString = await doAssertionRefresh({
        url: PortletStoreApi.getState().url
      }).then(response => response.data);
      console.debug(timeId, 'received success for doAssertionRefresh');

      // parse next refresh time:
      const nextSsoRefreshTime =
        (nextTimeStampAsString && new Date(nextTimeStampAsString)) || null;
      scheduleRefreshAssertionCall(nextSsoRefreshTime);
    } catch (error) {
      console.debug(timeId, 'received failure for doAssertionRefresh');
      if (retryCount > 0) {
        console.warn(
          timeId,
          'Trying to refresh SAML assertion again after refresh failure'
        );
        registerRefreshCallForDelay(10 * 1000, retryCount - 1);
        return;
      }
      notify({
        title: intl('loginhook.failure.refresh.title'),
        message: intl('loginhook.failure.refresh.title'),
        type: NotificationType.WARN,
        onClose: () => {
          window.location.href = SenseConstants.logoutUrl;
        }
      });
    }
  }, delay);
}
